import React, { useState, useEffect, useMemo } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import './App.css';
import Sidebar from './components/Sidebar';
import Home from './pages/Home';
import Initiate from './pages/Initiate';
import Dashboard from './pages/Dashboard';
import EvidenceCollection from './pages/EvidenceCollection';
import Interviews from './pages/Interviews';
import AutoScored from './pages/AutoScored';
import DocumentUploads from './pages/DocumentUploads';
import ManualInputs from './pages/ManualInputs';
import Report from './pages/Report';
import FrameworkReference from './pages/FrameworkReference';
import ToolkitLibrary from './pages/ToolkitLibrary';
import ToolkitAdmin from './pages/ToolkitAdmin';
import LandingPage from './pages/LandingPage';
import ProfilePage from './pages/ProfilePage';
import UserManagementPage from './pages/UserManagementPage';
import InternalPoolPage from './pages/InternalPoolPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import ActivatePage from './pages/ActivatePage';
import AdminTenantsPage from './pages/AdminTenantsPage';
import WorkflowStepper from './components/WorkflowStepper';
import TenantLifecycleBanner from './components/TenantLifecycleBanner';
import { useCurrentUser } from './auth/useCurrentUser';

const API = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5295';

const WORKFLOW_PAGES = new Set(['auto', 'uploads', 'interviews', 'manual', 'evidence', 'report']);

function getDynamicTitle(page) {
  const titles = {
    home: 'SAARA Portal',
    initiate: 'New engagement',
    dashboard: 'Assessment dashboard',
    evidence: 'Evidence review',
    interviews: 'Stakeholder interviews',
    auto: 'Auto-scored controls',
    uploads: 'Document uploads',
    manual: 'Manual inputs',
    report: 'Findings report & export',
    framework: 'SAARA Framework Reference',
    toolkit: 'Toolkit',
    'toolkit-admin': 'Toolkit administration',
    profile: 'Your profile',
    'user-management': 'User management',
    'internal-pool': 'Internal user pool',
    'tenants': 'Tenants',
  };
  return titles[page] || 'SAARA Portal';
}

// Role → pages this role can navigate to. Mirrors the backend authorisation
// model: Admin/Assessor/Presales/CSP can do everything in their tenant; Client
// is read-only on a curated subset. Platform-admin-only pages (internal-pool)
// are gated separately on the IsPlatformAdmin flag, not on role.
const ROLE_PAGES = {
  admin:    new Set(['home','dashboard','evidence','interviews','auto','uploads','manual','report','framework','initiate','toolkit','toolkit-admin','profile','user-management']),
  assessor: new Set(['home','dashboard','evidence','interviews','auto','uploads','manual','report','framework','initiate','toolkit','profile']),
  presales: new Set(['home','dashboard','report','framework','toolkit','profile']),
  csp:      new Set(['home','dashboard','report','framework','toolkit','profile']),
  client:   new Set(['home','dashboard','interviews','report','framework','toolkit','profile']),
};

function getInviteCodeFromUrl() {
  if (typeof window === 'undefined') return null;
  if (!window.location.pathname.startsWith('/accept-invite')) return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('code');
}

function getActivateTokenFromUrl() {
  if (typeof window === 'undefined') return null;
  if (!window.location.pathname.startsWith('/activate')) return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
}

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const [inviteCode] = useState(() => getInviteCodeFromUrl());
  const [activateToken] = useState(() => getActivateTokenFromUrl());

  // Public Marketplace activation page is special — it surfaces both before
  // and after sign-in (MSAL gates the activation POST inside the page).
  if (activateToken) {
    return (
      <>
        <ActivatePage token={activateToken} />
        <TenantLifecycleBanner />
      </>
    );
  }

  // Public landing page — anonymous users see this.
  if (!isAuthenticated) {
    if (inProgress !== 'none') {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7A8899', fontSize: 13 }}>
          Signing you in…
        </div>
      );
    }
    if (inviteCode) return <AcceptInvitePage inviteCode={inviteCode} />;
    return <LandingPage />;
  }

  return (
    <>
      <AuthenticatedApp inviteCode={inviteCode} />
      <TenantLifecycleBanner />
    </>
  );
}

function AuthenticatedApp({ inviteCode }) {
  const { profile, role, isPlatformAdmin, activeTenant, loading: userLoading } = useCurrentUser();
  const allowedPages = ROLE_PAGES[role] || new Set(['home', 'profile']);

  const [page, setPage] = useState(inviteCode ? 'accept-invite' : 'home');
  const [overview, setOverview] = useState(null);
  const [domains, setDomains] = useState([]);
  const [engagement, setEngagement] = useState(() => {
    try { const s = localStorage.getItem('saara_engagement'); return s ? JSON.parse(s) : null; }
    catch { return null; }
  });

  const refresh = async () => {
    if (!activeTenant) return;
    try {
      const [ov, dm] = await Promise.all([
        fetch(`${API}/api/overview`).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/domains`).then(r => r.ok ? r.json() : []),
      ]);
      if (ov) setOverview(ov);
      if (dm) setDomains(dm);
    } catch (e) { console.error('API error', e); }
  };

  useEffect(() => { refresh(); }, [activeTenant]);

  const syncEngagement = async (eng) => {
    try {
      await fetch(`${API}/api/engagement`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          org_name: eng.orgName, assessor: eng.assessor, industry: eng.industry,
          country: eng.country,
          licensing: eng.microsoftLicensing || eng.googleLicensing || eng.awsLicensing || '',
          tech_stack: eng.techStack || eng.stackLabel || '',
          eng_name: eng.engName, eng_type: eng.engType, eng_date: eng.engDate,
          office: eng.office, contact_name: eng.contactName, contact_email: eng.contactEmail,
        }),
      });
    } catch (e) { console.error('Engagement sync failed', e); }
  };

  // eslint-disable-next-line
  useEffect(() => { if (engagement) syncEngagement(engagement); }, []);

  const handleBegin = async (engData) => {
    setEngagement(engData);
    localStorage.setItem('saara_engagement', JSON.stringify(engData));
    try {
      await fetch(`${API}/api/engagements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          org_name: engData.orgName, assessor: engData.assessor, industry: engData.industry, country: engData.country,
          licensing: engData.microsoftLicensing || engData.googleLicensing || engData.awsLicensing || '',
          tech_stack: engData.techStack || engData.stackLabel || '',
          eng_name: engData.engName, eng_type: engData.engType, eng_date: engData.engDate,
          office: engData.office, contact_name: engData.contactName, contact_email: engData.contactEmail,
          regulatory_jurisdictions: engData.regulatoryJurisdictions ? JSON.stringify(engData.regulatoryJurisdictions) : null,
          ai_system_count: typeof engData.aiSystemCount === 'number' ? engData.aiSystemCount : null,
          assessment_trigger: engData.assessmentTrigger || null, eng_tier: engData.engTier || null,
          data_residency: engData.dataResidency || null,
        }),
      });
    } catch (e) { console.error('Engagement creation failed', e); }
    setPage('auto');
    refresh();
  };

  const handleStartRecertification = async () => {
    try {
      const eng = await fetch(`${API}/api/engagements/active`).then(r => r.json());
      if (!eng || !eng.id) { alert('No active engagement found'); return; }
      const res = await fetch(`${API}/api/runs/${eng.id}/start`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to start recertification run');
      const run = await res.json();
      alert(`Recertification Run ${run.run_number} started. Update controls that have changed.`);
      refresh();
      setPage('dashboard');
    } catch (e) { console.error(e); alert('Failed to start recertification — check backend'); }
  };

  const handleReset = async () => {
    try {
      const eng = await fetch(`${API}/api/engagements/active`).then(r => r.json());
      if (eng && eng.id) await fetch(`${API}/api/engagements/${eng.id}`, { method: 'DELETE' });
    } catch (e) { console.error(e); }
    setEngagement(null);
    localStorage.removeItem('saara_engagement');
    setPage('home');
    setOverview(null);
    setDomains([]);
  };

  // Route guard: bounce off pages the role can't see, and off platform-admin
  // pages without the flag.
  useEffect(() => {
    if ((page === 'internal-pool' || page === 'tenants') && !isPlatformAdmin) setPage('home');
    if (page === 'toolkit-admin' && role !== 'admin') setPage('toolkit');
    if (page !== 'accept-invite' && page !== 'profile'
        && page !== 'internal-pool' && page !== 'tenants'
        && !allowedPages.has(page)) {
      setPage('home');
    }
  }, [page, role, isPlatformAdmin, allowedPages]);

  const hasEng = !!engagement;
  const clientName = engagement?.orgName || '—';
  const assessor = engagement?.assessor || '—';
  const engDate = engagement?.engDate
    ? new Date(engagement.engDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    : '';

  const dynamicTitle = useMemo(() => getDynamicTitle(page), [page]);

  const pages = {
    dashboard: Dashboard, evidence: EvidenceCollection, interviews: Interviews,
    auto: AutoScored, uploads: DocumentUploads, manual: ManualInputs, report: Report,
    framework: FrameworkReference,
    toolkit: ToolkitLibrary, 'toolkit-admin': ToolkitAdmin,
    profile: ProfilePage,
    'user-management': UserManagementPage,
    'internal-pool': InternalPoolPage,
    'tenants': AdminTenantsPage,
  };
  const PageComp = pages[page];

  if (page === 'accept-invite' && inviteCode) {
    return <AcceptInvitePage inviteCode={inviteCode} onAccepted={() => setPage('home')} />;
  }

  if (userLoading && !profile) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7A8899', fontSize: 13 }}>
        Loading your profile…
      </div>
    );
  }

  // Edge case: signed in but holding zero tenant assignments. Stay on profile
  // page so the user can see what's going on instead of an empty shell.
  const noTenants = !activeTenant;

  return (
    <div className="app">
      <Sidebar
        page={page}
        setPage={setPage}
        overview={overview}
        engagement={engagement}
        role={role}
        isPlatformAdmin={isPlatformAdmin}
        allowedPages={allowedPages}
        onNewEngagement={() => setPage('initiate')}
        onReset={handleReset}
        onStartRecertification={handleStartRecertification}
      />
      <div className="main">
        {page !== 'initiate' && page !== 'home' && (
          <div className="topbar">
            <div>
              <div className="topbar-title">{dynamicTitle}</div>
              {hasEng && (
                <div className="topbar-meta">
                  {clientName} {engDate && `· ${engDate}`} {assessor && `· Lead assessor: ${assessor}`} · Kloudynet
                  {engagement?.engType && engagement.engType !== 'New Assessment' && (
                    <span className="pill pill-blue" style={{ marginLeft: 8, fontSize: 9 }}>{engagement.engType}</span>
                  )}
                  {overview?.is_recertification && (
                    <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#E8F5E9', color: '#2E7D32', fontWeight: 500 }}>
                      Run {overview.run_number} — Recertification
                    </span>
                  )}
                  {overview?.unconfirmed_count > 0 && (
                    <span style={{ marginLeft: 12, fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#FFF8E1', color: '#F57F17', fontWeight: 500 }}>
                      {overview.unconfirmed_count} auto-scored controls need assessor review
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="topbar-actions">
              <button className="btn-secondary" onClick={refresh}>↻ Refresh</button>
            </div>
          </div>
        )}

        {hasEng && role === 'assessor' && WORKFLOW_PAGES.has(page) && (
          <WorkflowStepper currentPage={page} setPage={setPage} workflow={overview?.workflow} />
        )}

        <div className="content">
          {noTenants && page !== 'profile' ? (
            <div style={{ padding: 32, maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 }}>
                Welcome, {profile?.displayName || profile?.email || 'there'}
              </div>
              <div style={{ fontSize: 12, color: '#7A8899', lineHeight: 1.6, marginBottom: 16 }}>
                You're signed in but don't yet have access to any tenant.
                An admin needs to assign you, or your marketplace activation may still be in progress.
              </div>
              <button className="btn-primary" onClick={() => setPage('profile')}>View profile</button>
            </div>
          ) : page === 'home' ? (
            <Home
              API={API}
              engagement={engagement}
              overview={overview}
              role={role}
              displayName={profile?.displayName || profile?.email || ''}
              onNewAssessment={() => setPage('initiate')}
              onRecertify={handleStartRecertification}
              onContinue={() => setPage('dashboard')}
              onClientView={(pg) => { if (allowedPages.has(pg)) setPage(pg); }}
            />
          ) : page === 'initiate' ? (
            <Initiate onBegin={handleBegin} existingEngagement={engagement} />
          ) : PageComp ? (
            <PageComp
              API={API}
              domains={domains}
              overview={overview}
              refresh={refresh}
              engagement={engagement}
              setPage={setPage}
              role={role}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
